import { DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';

import {
  CatalogApi,
  GetEntitiesRequest,
  GetEntitiesResponse,
} from '@backstage/catalog-client';
import {
  CompoundEntityRef,
  Entity,
  getCompoundEntityRef,
  isComponentEntity,
  RELATION_HAS_PART,
  RELATION_MEMBER_OF,
  RELATION_OWNER_OF,
} from '@backstage/catalog-model';
import { getEntityRelations } from '@backstage/plugin-catalog-react';

import { TechInsightsClient } from '@backstage-community/plugin-tech-insights-react';
import {
  BulkCheckResponse,
  CheckResult,
} from '@backstage-community/plugin-tech-insights-common';

import { CustomCheckResult } from '@internal/plugin-tech-insights-backend-module-maturity';

import {
  BulkMaturityScore,
  BulkMaturitySummary,
  MaturityRank,
  MaturityScore,
  MaturitySummary,
} from '../types';
import { ScoringDataFormatter } from './ScoringDataFormatter';
import { ScoringDataApi } from './ScoringDataApi';

const SDF = new ScoringDataFormatter();

export class ScoringDataClient implements ScoringDataApi {
  discoveryApi: DiscoveryApi;
  identityApi: IdentityApi;
  catalogApi: CatalogApi;
  techInsightsClient: TechInsightsClient;

  constructor({
    catalogApi,
    discoveryApi,
    identityApi,
  }: {
    catalogApi: CatalogApi;
    discoveryApi: DiscoveryApi;
    identityApi: IdentityApi;
  }) {
    this.catalogApi = catalogApi;
    this.discoveryApi = discoveryApi;
    this.identityApi = identityApi;
    this.techInsightsClient = new TechInsightsClient({
      discoveryApi,
      identityApi,
    });
  }

  public async getMaturityRank(entity: Entity): Promise<MaturityRank> {
    const checksResult = (await this.getCheckResults(
      entity,
    )) as CustomCheckResult[];
    return SDF.getMaturityRank(checksResult);
  }

  public async getMaturityScore(entity: Entity): Promise<MaturityScore> {
    const checksResult = (await this.getCheckResults(
      entity,
    )) as CustomCheckResult[];
    return SDF.generateMaturityScore(checksResult);
  }

  public async getMaturitySummary(entity: Entity): Promise<MaturitySummary> {
    const checksResult = (await this.getCheckResults(
      entity,
    )) as CustomCheckResult[];
    return SDF.generateMaturitySummary(checksResult);
  }

  private async getCheckResults(entity: Entity): Promise<CheckResult[]> {
    if (isComponentEntity(entity)) {
      return this.techInsightsClient.runChecks(getCompoundEntityRef(entity));
    }

    const entities = await this.getRelatedComponents(entity);
    return (await this.getGroupCheckResults(entities)) as CustomCheckResult[];
  }

  private async getGroupCheckResults(
    entities: CompoundEntityRef[],
  ): Promise<CheckResult[]> {
    /**
     * Passing an empty array into techInsightsClient.runBulkChecks()
     * now causes checks to run across the entire catalog.
     * We don't want to run checks here if no entities were provided.
     */
    if (entities.length === 0) {
      return [];
    }

    const results: CheckResult[] = [];
    const bulkResponse = await this.techInsightsClient.runBulkChecks(entities);
    for (const response of bulkResponse) {
      Array.prototype.push.apply(results, response.results);
    }

    return results;
  }

  private async getBulkCheckResults(
    entities: CompoundEntityRef[],
  ): Promise<BulkCheckResponse> {
    return this.techInsightsClient.runBulkChecks(entities);
  }

  public async getBulkMaturityScores(
    entity: Entity,
  ): Promise<BulkMaturityScore> {
    const entities = await this.getRelatedComponents(entity);
    const checksResult = await this.getBulkCheckResults(entities);
    return Promise.all(
      checksResult.map(async x => {
        const checkedEntity = await this.catalogApi.getEntityByRef(x.entity);

        return {
          // Stub entity object from entity reference string if catalogApi fails check
          entity: checkedEntity ?? {
            apiVersion: 'backstage.io/v1alpha1',
            kind: x.entity.split(':')[0],
            metadata: {
              name: x.entity.split('/')[1],
              namespace: 'default',
              title: x.entity.split('/')[1],
            },
          },
          entityRef: x.entity,
          score: SDF.generateMaturityScore(x.results as CustomCheckResult[]),
        };
      }),
    );
  }

  public async getBulkMaturitySummary(
    entity: Entity,
  ): Promise<BulkMaturitySummary> {
    const entities = await this.getRelatedComponents(entity);
    const checksResult = await this.getBulkCheckResults(entities);
    return checksResult.map(x => ({
      // Create entity object from entity reference string returned in BulkCheckReponse
      entity: {
        apiVersion: 'backstage.io/v1alpha1',
        kind: x.entity.split(':')[0],
        metadata: {
          name: x.entity.split('/')[1],
          namespace: 'default',
        },
      },
      summary: SDF.generateMaturitySummary(x.results as CustomCheckResult[]),
    }));
  }

  private async getRelatedComponents(
    entity: Entity,
  ): Promise<CompoundEntityRef[]> {
    switch (entity.kind) {
      case 'System':
        return this.getComponentsForSystem(entity);
      case 'Domain':
        return this.getComponentsForDomain(entity.metadata.name);
      case 'Group':
        return this.getComponentsForGroup(entity);
      case 'User':
        return this.getComponentsForUser(entity);
      default:
        return [];
    }
  }

  private async getComponentsForSystem(
    entity: Entity,
  ): Promise<CompoundEntityRef[]> {
    return getEntityRelations(entity, RELATION_HAS_PART, { kind: 'Component' });
  }

  private async getComponentsForDomain(
    domainName: string,
  ): Promise<CompoundEntityRef[]> {
    const request: GetEntitiesRequest = {
      filter: [
        {
          kind: 'component',
          'metadata.annotations.mdsol/domain': domainName,
        },
      ],
    };
    const response: GetEntitiesResponse = await this.catalogApi.getEntities(
      request,
    );
    return response.items.map(x => getCompoundEntityRef(x));
  }

  private async getComponentsForOrganization(): Promise<CompoundEntityRef[]> {
    // Getting all component entities would be the fastest
    const request: GetEntitiesRequest = {
      filter: [{ kind: 'component' }],
    };
    const response: GetEntitiesResponse = await this.catalogApi.getEntities(
      request,
    );
    return response.items.map(x => getCompoundEntityRef(x));
  }

  private async getComponentsSolutonLine(
    entity: Entity,
  ): Promise<CompoundEntityRef[]> {
    const components: CompoundEntityRef[] = [];

    const domains = getEntityRelations(entity, RELATION_OWNER_OF, {
      kind: 'Domain',
    });
    for (const domain of domains) {
      Array.prototype.push.apply(
        components,
        await this.getComponentsForDomain(domain.name),
      );
    }

    return components;
  }

  private async getComponentsForGroup(
    entity: Entity,
  ): Promise<CompoundEntityRef[]> {
    if (entity.spec?.type === 'solution-line') {
      return this.getComponentsSolutonLine(entity);
    } else if (entity.spec?.type === 'organization') {
      return this.getComponentsForOrganization();
    }

    return getEntityRelations(entity, RELATION_OWNER_OF, { kind: 'Component' });
  }

  private async getComponentsForUser(
    entity: Entity,
  ): Promise<CompoundEntityRef[]> {
    const teams = getEntityRelations(entity, RELATION_MEMBER_OF, {
      kind: 'Group',
    });
    const components = teams.map(async team => {
      const teamEntity = await this.catalogApi.getEntityByRef(team);
      return getEntityRelations(teamEntity, RELATION_OWNER_OF, {
        kind: 'Component',
      });
    });

    return (await Promise.all(components)).flat();
  }
}
